// =====================================================================================
// INVRT Radar — web console — Stripe checkout return (success)
//
// Stripe redirects the browser here after a successful card payment (the success_url the
// Credits tab sent on POST /v1/packs/checkout). NOTHING has been granted yet at this point
// — checkout.ts's own contract is explicit that the credit grant happens later, once,
// when Stripe's signed checkout.session.completed reaches the stripe-webhooks receiver —
// so the balance is explicitly refetched rather than trusted from whatever the header chip
// last cached, and the copy says plainly that a short wait is normal.
// =====================================================================================

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useBalance } from "@/hooks/useApi";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState, LoadingState } from "@/components/States";
import { formatCredits } from "@/lib/format";

export function CheckoutSuccessPage() {
  const { data, isLoading, isError, error } = useBalance();
  const queryClient = useQueryClient();

  useEffect(() => {
    // INVALIDATION, deliberately — not a bare refetch(). At mount the session (and with it
    // the balance query's org-scoped key) may not have resolved yet: useBalance is disabled
    // until orgId exists, and a refetch() against a not-yet-enabled query is a silent no-op
    // that never re-fires — the race this page's first version lost. Marking the key stale
    // instead makes React Query re-read the balance the moment the query IS active, whenever
    // that turns out to be, bypassing useBalance's own staleTime exactly as intended.
    void queryClient.invalidateQueries({ queryKey: ["balance"] });
  }, [queryClient]);

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center gap-6 py-12 text-center">
      <Card className="w-full">
        <CardHeader className="items-center text-center">
          <CheckCircle2 className="h-8 w-8 text-accent" aria-hidden="true" />
          <CardTitle>Purchase complete</CardTitle>
          <p className="mt-1 text-sm text-ink-muted">
            Stripe has confirmed your payment. Credits can take a few moments to land — the
            webhook that grants them runs just after checkout, not before this page loads.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-muted">Current balance</p>
            {/* The every-state law: the wait and the failure are canonical states with
                their own roles, never bare words. Retry re-runs the SAME invalidateQueries
                path the page mounts with, so a recovered grant lands without leaving. */}
            {isLoading ? (
              <LoadingState label="Checking your balance…" className="justify-center" />
            ) : isError ? (
              <ErrorState
                error={error}
                onRetry={() => void queryClient.invalidateQueries({ queryKey: ["balance"] })}
              />
            ) : (
              <p className="text-2xl font-semibold text-ink">{formatCredits(data?.balance)}</p>
            )}
          </div>
          <Link to="/account?tab=credits" className={buttonVariants({ variant: "primary" })}>
            Back to credits
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
